/**
 * `FeedClient` (§14.8.3–§14.8.7): ciclo do snapshot (uma busca em voo por vez), aceitação por `seq`/`epoch` (I-03),
 * `ops` validado à parte e só depois da validação (M9), arquivos secundários sem bloquear, eventos provisórios,
 * `selectView`, estado de conexão (§10.6) e replay (`at`). Não conhece sinais nem o DOM: entrega tudo por callback
 * (`onUpdate`) ao `app/feedBinding.ts`, que escreve `feed`/`liveView`/`historySeries` num único `batch()` (DE-B06).
 * A única global lida é `window.__snapshotPrefetch` (pré-busca inline, §15.7.4). Os eventos da página (aba oculta,
 * rede, bfcache) chegam pelos métodos `setVisible`, `setOnline`, `pageShow` e `pageHide`; `attachLifecycle` os liga.
 *
 * Relógios: `FeedState.lastSuccessAt`/`lastChangeAt` e a derivação de conexão usam o relógio da FONTE
 * (`source.clock()`); intervalos e limites de taxa (20 s, 30 s, 45 s, 60 s, 5 min) usam o relógio do aparelho
 * (`Date.now`, o mesmo dos temporizadores).
 */

import type { ArchiveIndex } from '../contracts/archive';
import { type CandidateNumber, type IbgeCode, type Office, type PlaceCode, tMin } from '../contracts/base';
import type { CommentaryIndex } from '../contracts/commentary';
import type { ElectionEvent } from '../contracts/events';
import type { HistoryPoint } from '../contracts/history';
import type { Insight, InsightIndex } from '../contracts/insights';
import { type MunicipalColumns, type StateDetail, type ZoneResults, ZoneResultsSchema } from '../contracts/municipal';
import type { ProbabilityDetail } from '../contracts/probability';
import type { Ops, Snapshot } from '../contracts/snapshot';
import type { CandidateList, Geography } from '../contracts/static';
import {
  parseFeedJson,
  type Validated,
  type ValidationContext,
  type ValidationIssue,
  validateArchiveIndex,
  validateEvents,
  validateHistory,
  validateMunicipal,
  validateProbabilityDetail,
  validateSnapshot,
  validateStateDetail,
} from '../contracts/validate';
import { epochMs, T_OPEN } from '../model/time';
import type { FeedState, ResultsView } from '../model/types';
import { type ConnectionInputs, deriveConnection } from './connection';
import {
  addEvents,
  type DeriveMemo,
  deriveEvents,
  deriveFromHistory,
  EMPTY_MEMO,
  EVENT_RULES,
  type EventRulesConfig,
  mergeEvents,
} from './deriveEvents';
import {
  type HeadlineMemoryLike,
  type HeadlinePolicy,
  liveInstantTMin,
  type ProbabilityPolicy,
  selectViewWithMemory,
} from './selectView';
import { type DataSource, SourceError, type SourceResponse } from './source';

export type { FeedNotice, FeedState } from '../model/types';

export const FEED_DEFAULTS = {
  pollIntervalMs: 15_000,
  jitter: [0.8, 1.2],
  backoffBaseMs: 5_000,
  backoffMaxMs: 30_000,
  retryAfterBoundsMs: [1_000, 300_000],
  timeoutMs: 10_000,
  maxBytes: 6_000_000,
  stalledCheckMs: 15_000,
  completeIntervalMs: 300_000,
  municipalMinIntervalMs: 20_000,
  historyIntervalMs: 300_000,
  eventsMinIntervalMs: 30_000,
  detailTtlMs: 45_000,
  detailRetryBaseMs: 10_000,
  detailRetryMaxMs: 60_000,
  failoverAfterFailures: 3,
  replayCacheSize: 16,
  archiveIndexTtlMs: 60_000,
  simEmitMinIntervalMs: { 1: 0, 5: 0, 15: 2_000, 60: 6_000, 300: 6_000 },
  /** Extensões desta implementação (§15.7.4, §14.8.4). */
  prefetchMaxAgeMs: 10_000,
  contextDelayMs: 1_500,
  simMinPollMs: 250,
  detailCacheSize: 8,
} as const;

export interface ReplayResult {
  view: ResultsView;
  requestedTMin: number;
  shownTMin: number;
  seq: number;
}

/** Falha do replay: sem registro (o chamador mostra `notice.noRecord` e volta ao vivo), resposta superada ou inválida. */
export class ReplayError extends Error {
  constructor(readonly code: 'E_NO_RECORD' | 'E_STALE' | 'E_INVALID') {
    super(code);
    this.name = 'ReplayError';
  }
}

export interface FeedClient {
  start(): void;
  /** Aborta tudo e cancela os temporizadores. */
  stop(): void;
  /** "Tentar de novo"; volta de visibilidade, online e bfcache. */
  refreshNow(): Promise<void>;
  /** §14.8.7. */
  at(tMin: number, signal?: AbortSignal): Promise<ReplayResult>;
  /** Cache de 45 s, LRU de 8. */
  stateDetail(place: PlaceCode, office?: Office): Promise<StateDetail | null>;
  /** P2. */
  zones(ibge: IbgeCode): Promise<ZoneResults | null>;
  /**
   * Situação incomum (P-27): o índice do snapshot atual (`files.insights`), um pedido por seq (o mesmo seq devolve o
   * que já veio). Quem pede passa o validador (validate.insights.ts fica fora do JS crítico, no chunk do painel).
   * null = camada desligada, falha ou arquivo inválido.
   */
  insightIndex(validate: InsightValidator<InsightIndex>): Promise<InsightIndex | null>;
  /** O pacote de um sinal (`path` do índice, relativo à pasta dele; imutável, guardado). null = falha ou inválido. */
  insight(path: string, validate: InsightValidator<Insight>): Promise<Insight | null>;
  /**
   * Comentários ao vivo (P-32): o índice do snapshot atual (`files.commentary`), um pedido por seq. O validador vem de
   * quem pede (validate.commentary.ts, no chunk do painel). null = camada desligada, falha ou arquivo inválido.
   */
  commentaryIndex(validate: InsightValidator<CommentaryIndex>): Promise<CommentaryIndex | null>;
  /**
   * P-39: um arquivo imutável das horas fechadas dos comentários (`commentary/h/hours-{sha8}.json` ou
   * `commentary/h/{AAAAMMDDHH}-{sha8}.json`), guardado depois do 1º pedido válido. O validador vem de quem pede.
   * null = caminho fora da pasta, camada desligada, falha ou arquivo inválido.
   */
  commentaryFile<T>(path: string, validate: InsightValidator<T>): Promise<T | null>;
  /** M2: o mapa declara que precisa da camada municipal. */
  wantMunicipal(on: boolean): void;
  /** ProbabilitySlot visível. */
  wantProbabilityDetail(on: boolean): void;
  // ── Extensões (ligação com app/feedBinding.ts) ──
  /** Aba visível ou oculta (`visibilitychange`, `freeze`/`resume`). */
  setVisible(visible: boolean): void;
  /** Eventos `online`/`offline`. */
  setOnline(online: boolean): void;
  /** `pageshow` (com `persisted` = volta do bfcache). */
  pageShow(persisted: boolean): void;
  /** `pagehide` (com `persisted` = vai para o bfcache). */
  pageHide(persisted: boolean): void;
  /** Etapa 3 de §15.7.4 (depois do mapa leve): histórico e eventos. Sem a chamada, 1,5 s depois do 1º número. */
  contextReady(): void;
  /** Recalcula `connection` (ex.: `timeline.live` mudou) e avisa se mudou. */
  refreshConnection(): void;
  /** Refaz a vista com os estáticos atuais (ex.: a geografia chegou). */
  refreshView(): void;
  state(): FeedState;
  view(): ResultsView | null;
  probabilityDetail(): ProbabilityDetail | null;
}

/** O que muda num ciclo; o binding grava tudo num único `batch()`. Campo ausente = sem mudança. */
export interface FeedUpdate {
  feed: FeedState;
  liveView?: ResultsView;
  historySeries?: readonly HistoryPoint[];
  probabilityDetail?: ProbabilityDetail | null;
  /** `files.insights` do snapshot atual (P-27): null = camada desligada. */
  insightsPath?: string | null;
  /** `files.commentary` do snapshot atual (P-32): null = comentários desligados. */
  commentaryPath?: string | null;
  /**
   * "Desligar lento" (docs/DECISOES.md §75): sobe quando o índice dos comentários, relido com o `seq` parado, saiu de
   * `active` (ou de `paused` para `off`). O painel pede o índice de novo e recebe o estado adotado.
   */
  commentaryRev?: number;
}

/** Validador de um arquivo de insights, injetado por quem pede (o chunk do painel; FeedClient.insightIndex). */
export type InsightValidator<T> = (raw: unknown, ctx: ValidationContext) => Validated<T>;

/** O índice ao vivo mora em `insights/` (P-27); nada fora da pasta, nada com `..`. */
const INSIGHT_INDEX_PATH = /^insights\/[a-z0-9_-]+\.json$/;
/** O pacote de um sinal, relativo à pasta do índice: `{id de 32 hex}/v{n}.json` (insights.ts). */
const INSIGHT_SIGNAL_PATH = /^[0-9a-f]{32}\/v[1-9]\d{0,5}\.json$/;
/** P-39: as horas fechadas moram em `commentary/h/`, com o sha8 no nome (imutáveis). */
const COMMENTARY_FILE_PATH = /^commentary\/h\/(?:hours-[0-9a-f]{8}|\d{10}-[0-9a-f]{8})\.json$/;
/** Teto do cache das horas (uma noite tem umas 8 horas fechadas e a lista). */
const COMMENTARY_FILE_CACHE_SIZE = 32;
/** O índice dos comentários mora em `commentary/` (P-32); nada fora da pasta. */
const COMMENTARY_INDEX_PATH = /^commentary\/[a-z0-9_-]+\.json$/;
/** Pacotes de sinal guardados (imutáveis). */
const INSIGHT_CACHE_SIZE = 200;

/** `window.__snapshotPrefetch` (§15.7.4). */
export interface SnapshotPrefetch {
  startedAt: number;
  response: Promise<string | null>;
  /** Preenchidos na chegada da resposta (pré-mortem ORD-F-148 M4 e B5): status HTTP, `Date`, `Age` e a hora local. */
  status?: number;
  date?: string | null;
  age?: string | null;
  at?: number;
}

/** M3 (ORD-F-148): a 0% depois de 17h10, a contagem parada já vale como "sem números novos". */
const ZERO_LATE_MS = 10 * 60_000;

export interface FeedClientConfig {
  mode: 'live' | 'simulation';
  build: string;
  poll_interval_s: number;
  election_date: string;
  round: 1 | 2;
  features: { probability: boolean; governors: boolean };
}

export interface FeedClientOptions {
  source: DataSource;
  config: FeedClientConfig;
  candidates: CandidateList;
  /** Geografia quando chegar (etapa 3). */
  geography?: () => Geography | null;
  /** `!__SIM_ENABLED__`. */
  isProductionBuild: boolean;
  onUpdate(u: FeedUpdate): void;
  /** `ops.reload_build` ≠ build num snapshot VÁLIDO (M9): o binding aplica a trava de sessionStorage e recarrega. */
  onReloadRequested?(build: string, delayMs: number): void;
  timelineLive?: () => boolean;
  /** Visibilidade na partida (padrão: visível). */
  hidden?: () => boolean;
  /** Rede na partida e nas falhas (padrão: navigator.onLine). */
  online?: () => boolean;
  /**
   * Piso do intervalo entre buscas do snapshot (também no recuo depois de falha), lido a cada agendamento. A página
   * inicial (antes de 25/10) usa 5 min: o feed ainda não existe e cada busca volta 404 (pré-mortem, ORD-F-148 B5).
   */
  minDelayMs?: () => number;
  /** Pré-busca (padrão: `window.__snapshotPrefetch`, consumida uma vez, só no http). */
  prefetch?: () => SnapshotPrefetch | null | undefined;
  /** Relógio do aparelho (padrão: Date.now). */
  now?: () => number;
  /** U(0, 1) do jitter e do atraso da recarga (padrão: Math.random). */
  random?: () => number;
  rules?: EventRulesConfig;
  headlinePolicy?: HeadlinePolicy;
  probabilityPolicy?: ProbabilityPolicy;
  log?: Pick<Console, 'error' | 'warn'>;
}

const EMPTY_STATE: FeedState = Object.freeze({
  connection: 'connecting',
  source: '',
  seq: null,
  epoch: null,
  lastSuccessAt: null,
  lastChangeAt: null,
  failures: 0,
  reason: null,
  notice: null,
  held: [],
  online: true,
  sourceStatus: null,
  verifiedAt: null,
  tseHtTMin: null,
  meanSeqIntervalMs: null,
  allComplete: false,
});

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;
/** O `Date` da pré-busca do boot, em ms; null sem ele. */
function prefetchDateMs(pre: SnapshotPrefetch): number | null {
  const ms = pre.date ? Date.parse(pre.date) : Number.NaN;
  return Number.isFinite(ms) ? ms : null;
}
/** A hora do dado de um seq novo (ORD-F-147): o `published_at`, nunca depois de agora (relógio da fonte). */
function dataAt(snap: Snapshot, nowMs: number): number {
  const pub = Date.parse(snap.clocks.published_at);
  return Number.isFinite(pub) ? Math.min(nowMs, pub) : nowMs;
}
const msOrNull = (iso: string | null | undefined) => (iso ? Date.parse(iso) : null);
const isAbort = (e: unknown) => (e as { name?: string } | null)?.name === 'AbortError';

/** Lê e consome `window.__snapshotPrefetch` (só existe com `mode = 'live'`). */
function defaultPrefetch(): SnapshotPrefetch | null {
  const g = globalThis as { __snapshotPrefetch?: SnapshotPrefetch };
  const p = g.__snapshotPrefetch;
  if (!p) return null;
  g.__snapshotPrefetch = undefined;
  return p;
}

/** Ponto do histórico montado de um snapshot aceito (mesma forma de history.json, §14.5). */
export function historyPointOf(s: Snapshot): HistoryPoint | null {
  const ht = s.clocks.tse_ht;
  if (ht === null) return null;
  const n = s.races.president.national;
  const [a, b] = s.races.president.candidates;
  const va = n.votes[a] ?? 0;
  const vb = n.votes[b] ?? 0;
  const p = s.probability;
  return {
    seq: s.seq,
    tse_ht: ht,
    t_min: tMin(ht, s.election.date),
    sections_fraction: round6(n.sections > 0 ? n.sections_counted / n.sections : 0),
    electorate_fraction: round6(n.electorate > 0 ? n.electorate_counted / n.electorate : 0),
    votes: [va, vb],
    valid_votes: va + vb,
    status: n.status,
    probability:
      p && p.status === 'active' && p.win_prob && p.final_share
        ? {
            status: p.status,
            base_seq: p.base_seq,
            win_prob_b: p.win_prob[b] ?? 0,
            final_share_b: {
              q10: p.final_share[b]?.q10 ?? 0,
              q50: p.final_share[b]?.q50 ?? 0,
              q90: p.final_share[b]?.q90 ?? 0,
            },
          }
        : null,
  };
}

/** `history.json` ∪ pontos dos snapshots aceitos, deduplicado por `seq` (o do arquivo vence), em `seq` crescente. */
export function mergeSeries(fromFile: readonly HistoryPoint[], local: readonly HistoryPoint[]): HistoryPoint[] {
  const bySeq = new Map<number, HistoryPoint>();
  for (const p of local) bySeq.set(p.seq, p);
  for (const p of fromFile) bySeq.set(p.seq, p);
  return [...bySeq.values()].sort((x, y) => x.seq - y.seq);
}

/**
 * "Desligar lento" (P-30 item 5; docs/DECISOES.md §75): na regravação do mesmo `seq`, o snapshot atual adota só o que
 * vai para o lado seguro — o bloco `probability` que passou a `null` ou a um status que não é `active`, e
 * `files.probability_detail` ou `files.commentary` que passaram a `null`. Nunca adota um `active` novo, um número novo
 * nem um caminho novo: religar espera o próximo `seq`. Devolve o snapshot com o que foi adotado, ou null se nada mudou.
 */
export function saferOnRewrite(cur: Snapshot, next: Snapshot): Snapshot | null {
  const a = cur.probability;
  const b = next.probability;
  // de um bloco que existia para null ou para outro bloco não ativo; de null para um bloco, nunca (é religar)
  const prob = a !== null && (b === null || b.status !== 'active') && JSON.stringify(a) !== JSON.stringify(b);
  // o detalhe só existe com `active` (P-30): sai junto com o bloco
  const detail = cur.files.probability_detail !== null && (next.files.probability_detail === null || prob);
  const commentary = cur.files.commentary != null && next.files.commentary == null;
  if (!prob && !detail && !commentary) return null;
  return {
    ...cur,
    probability: prob ? b : a,
    files: {
      ...cur.files,
      ...(detail ? { probability_detail: null } : {}),
      ...(commentary ? { commentary: null } : {}),
    },
  };
}

/** Fase 'final' (§13.1.2): Brasil 100% e governadores concluídos (ou desligados). */
function allCompleteOf(s: Snapshot, governorsOn: boolean): boolean {
  if (s.races.president.national.status !== 'complete') return false;
  if (!governorsOn) return true;
  return Object.values(s.races.governor ?? {}).every((g) => g?.status === 'complete');
}

/** Liga `visibilitychange`, `freeze`/`resume`, `online`/`offline` e `pageshow`/`pagehide` (sem `unload`, §10.9). */
export function attachLifecycle(fc: FeedClient, doc: Document, win: Window): () => void {
  const onVisibility = () => fc.setVisible(doc.visibilityState !== 'hidden');
  const onFreeze = () => fc.setVisible(false);
  const onResume = () => fc.setVisible(doc.visibilityState !== 'hidden');
  const onOnline = () => fc.setOnline(true);
  const onOffline = () => fc.setOnline(false);
  const onShow = (e: Event) => fc.pageShow((e as PageTransitionEvent).persisted === true);
  const onHide = (e: Event) => fc.pageHide((e as PageTransitionEvent).persisted === true);
  doc.addEventListener('visibilitychange', onVisibility);
  doc.addEventListener('freeze', onFreeze);
  doc.addEventListener('resume', onResume);
  win.addEventListener('online', onOnline);
  win.addEventListener('offline', onOffline);
  win.addEventListener('pageshow', onShow);
  win.addEventListener('pagehide', onHide);
  return () => {
    doc.removeEventListener('visibilitychange', onVisibility);
    doc.removeEventListener('freeze', onFreeze);
    doc.removeEventListener('resume', onResume);
    win.removeEventListener('online', onOnline);
    win.removeEventListener('offline', onOffline);
    win.removeEventListener('pageshow', onShow);
    win.removeEventListener('pagehide', onHide);
  };
}

interface CacheEntry<T> {
  at: number;
  value: T;
}

export function createFeedClient(o: FeedClientOptions): FeedClient {
  const D = FEED_DEFAULTS;
  const source = o.source;
  const cfg = o.config;
  const rules = o.rules ?? EVENT_RULES;
  const deviceNow = o.now ?? Date.now;
  const random = o.random ?? Math.random;
  const log = o.log ?? console;
  const geography = o.geography ?? (() => null);
  const timelineLive = o.timelineLive ?? (() => true);
  const takePrefetch = o.prefetch ?? defaultPrefetch;
  const navigatorOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  const isOnline = o.online ?? navigatorOnline;
  const date = cfg.election_date;

  // ── estado ──
  // FeedState.source: o tipo da fonte ('http' · 'sim' · 'replay' · 'fixture', como em model/types.ts); o id completo
  // ("http:feed.mapa.voto", "sim:base:2026") fica em DataSource.id.
  const sourceName = source.kind === 'simulation' ? 'sim' : source.kind;
  let state: FeedState = { ...EMPTY_STATE, source: sourceName, online: isOnline() };
  let started = false;
  let stopped = true;
  let hidden = o.hidden?.() ?? false;
  let suspended = false;
  let firstDone = false;
  let inflight: Promise<void> | null = null;
  /** Publicação avisada (push) durante uma busca em voo: busca de novo ao terminar. */
  let rerun = false;
  let cycleAbort: AbortController | null = null;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let stalledTimer: ReturnType<typeof setInterval> | null = null;
  let contextTimer: ReturnType<typeof setTimeout> | null = null;
  let historyTimer: ReturnType<typeof setTimeout> | null = null;
  let eventsTimer: ReturnType<typeof setTimeout> | null = null;
  let municipalTimer: ReturnType<typeof setTimeout> | null = null;
  let emitTimer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribe: (() => void) | null = null;
  let retryAfterMs: number | undefined;
  let pollHintMs: number | null = null;
  let originIdx = 0;

  let cur: Snapshot | null = null;
  let curEtag: string | null = null;
  let trio: [Snapshot | null, Snapshot | null] = [null, null];
  let memo: DeriveMemo = EMPTY_MEMO;
  let headlineMem: HeadlineMemoryLike | null = null;
  let liveView: ResultsView | null = null;

  let provisional: ElectionEvent[] = [];
  let published: ElectionEvent[] | null = null;
  let publishedSeq: number | null = null;
  let eventsFailed = false;
  let lastEventsAt = Number.NEGATIVE_INFINITY;
  let lastEventsSeq: number | null = null;
  let eventsInflight = false;

  let fileSeries: HistoryPoint[] = [];
  let localSeries: HistoryPoint[] = [];
  let series: HistoryPoint[] = [];
  let lastHistoryAt = Number.NEGATIVE_INFINITY;
  let historyInflight = false;

  let contextOn = false;
  let wantMun = false;
  let wantProb = false;
  let cols: MunicipalColumns | null = null;
  let colsDerived: MunicipalColumns | null = null;
  let colsPath: string | null = null;
  let lastMunicipalAt = Number.NEGATIVE_INFINITY;
  let municipalInflight = false;
  let probDetail: ProbabilityDetail | null = null;
  let probPath: string | null = null;
  let probInflight = false;
  /** `files.insights` já avisado ao binding (o sinal nasce null: só a camada ligada gera aviso). */
  let insightsPathSent: string | null = null;
  let insightIdx: { seq: number; path: string; value: InsightIndex } | null = null;
  let insightIdxInflight: { key: string; p: Promise<InsightIndex | null> } | null = null;
  const insightCache = new Map<string, Insight>();
  /** P-39: as horas fechadas dos comentários já validadas (imutáveis) e os pedidos em voo. */
  const commentaryFiles = new Map<string, unknown>();
  const commentaryFilesInflight = new Map<string, Promise<unknown>>();
  const insightInflight = new Map<string, Promise<Insight | null>>();
  /** `files.commentary` já avisado ao binding, e o último índice válido (um por seq). */
  let commentaryPathSent: string | null = null;
  let commentaryIdx: { seq: number; path: string; value: CommentaryIndex } | null = null;
  let commentaryInflight: { key: string; p: Promise<CommentaryIndex | null> } | null = null;
  /** §75: o validador que o painel passou (fica no chunk dele), a releitura em voo e a revisão avisada ao binding. */
  let commentaryValidate: InsightValidator<CommentaryIndex> | null = null;
  let commentaryRecheckInflight = false;
  let commentaryRev = 0;
  const reloadSeen = new Set<string>();

  const detailCache = new Map<string, CacheEntry<StateDetail>>();
  const detailFails = new Map<string, { k: number; retryAt: number }>();
  const detailInflight = new Map<string, Promise<StateDetail | null>>();
  const zonesCache = new Map<string, CacheEntry<ZoneResults>>();
  let archiveIndex: CacheEntry<ArchiveIndex> | null = null;
  const replayCache = new Map<number, ReplayResult['view']>();
  let atToken = 0;

  let lastEmitAt = Number.NEGATIVE_INFINITY;
  let pendingView: ResultsView | null = null;

  // ── utilidades ──
  const clock = () => source.clock();
  const speed = () => (source.kind === 'http' ? 1 : (source.control?.state().speed ?? 1));
  const pushMode = () => source.kind !== 'http' && speed() > 5 && unsubscribe !== null;
  const ctx = (current: Snapshot | null, serverNowMs: number | null = null): ValidationContext => ({
    config: { round: cfg.round, election_date: date, mode: cfg.mode },
    candidates: o.candidates,
    geography: geography(),
    current,
    isProductionBuild: o.isProductionBuild,
    serverNowMs,
  });
  const [ey, em, ed] = date.split('-').map(Number) as [number, number, number];
  const epoch = Date.UTC(ey, em - 1, ed, 3, 0, 0);
  const clockTMin = () => (clock() - epoch) / 60_000;
  const events = () => mergeEvents(provisional, published, publishedSeq);
  const clearTimer = (t: ReturnType<typeof setTimeout> | null) => {
    if (t !== null) clearTimeout(t);
    return null;
  };

  function connectionNow(s: FeedState): FeedState['connection'] {
    const n = cur?.races.president.national ?? null;
    const inputs: ConnectionInputs = {
      timelineLive: timelineLive(),
      mode: cfg.mode === 'simulation' || source.kind !== 'http' ? 'simulation' : 'live',
      online: s.online,
      hasData: liveView !== null,
      lastFetchFailed: s.failures > 0,
      counting: n !== null && n.sections_counted > 0 && n.sections_counted < n.sections,
      zeroLate:
        n !== null &&
        n.sections_counted === 0 &&
        clock() >= epochMs(cfg.election_date) + T_OPEN * 60_000 + ZERO_LATE_MS,
      nowMs: clock(),
      lastChangeAt: s.lastChangeAt,
      tseHtMs: msOrNull(cur?.clocks.tse_ht),
      verifiedAtMs: s.verifiedAt,
      sourceStatus: s.sourceStatus,
    };
    return deriveConnection(inputs);
  }

  function setState(patch: Partial<FeedState>): void {
    const next = { ...state, ...patch };
    next.connection = connectionNow(next);
    state = Object.freeze(next);
  }

  function emit(extra: Omit<FeedUpdate, 'feed'> = {}): void {
    o.onUpdate({ feed: state, ...extra });
  }

  /** Emite a vista nova; na simulação rápida, no máximo 1 vez a cada simEmitMinIntervalMs (saliência 3 na hora). */
  function emitView(view: ResultsView, urgent: boolean, extra: Omit<FeedUpdate, 'feed' | 'liveView'> = {}): void {
    const minMs = source.kind === 'http' ? 0 : (D.simEmitMinIntervalMs[speed() as 1 | 5 | 15 | 60 | 300] ?? 0);
    const t = deviceNow();
    if (urgent || minMs === 0 || t - lastEmitAt >= minMs) {
      emitTimer = clearTimer(emitTimer);
      pendingView = null;
      lastEmitAt = t;
      emit({ liveView: view, ...extra });
      return;
    }
    pendingView = view;
    emit(extra);
    if (emitTimer === null) {
      emitTimer = setTimeout(
        () => {
          emitTimer = null;
          if (pendingView && !stopped) {
            const v = pendingView;
            pendingView = null;
            lastEmitAt = deviceNow();
            emit({ liveView: v });
          }
        },
        Math.max(0, lastEmitAt + minMs - t),
      );
    }
  }

  function select(s: Snapshot): ResultsView {
    const r = selectViewWithMemory(s, {
      candidates: o.candidates,
      geography: geography(),
      municipal: cols,
      events: events(),
      instantTMin: liveInstantTMin(s),
      clockTMin: clockTMin(),
      probabilityOn: cfg.features.probability,
      headlineMem,
      mode: 'live',
      headlinePolicy: o.headlinePolicy,
      probabilityPolicy: o.probabilityPolicy,
    });
    headlineMem = r.headlineMem;
    return r.view;
  }

  function rebuildView(urgent = false): void {
    if (!cur || stopped) return;
    liveView = select(cur);
    emitView(liveView, urgent);
  }

  // ── agenda ──
  function canPoll(): boolean {
    return started && !stopped && !hidden && !suspended && state.online;
  }

  function nextDelay(): number | null {
    const floor = o.minDelayMs?.() ?? 0;
    if (state.failures > 0) {
      const backoff = Math.min(D.backoffMaxMs, D.backoffBaseMs * 2 ** (state.failures - 1));
      return Math.max(floor, retryAfterMs ?? backoff);
    }
    if (pushMode()) return null;
    let base = pollHintMs ?? (cfg.poll_interval_s > 0 ? cfg.poll_interval_s * 1000 : D.pollIntervalMs);
    if (source.kind !== 'http') base = Math.max(D.simMinPollMs, D.pollIntervalMs / speed());
    const [lo, hi] = D.jitter;
    let delay = base * (lo + (hi - lo) * random());
    if (state.allComplete) delay = Math.max(delay, D.completeIntervalMs);
    return Math.max(floor, delay);
  }

  function schedule(): void {
    pollTimer = clearTimer(pollTimer);
    if (!canPoll()) return;
    const delay = nextDelay();
    if (delay === null) return;
    pollTimer = setTimeout(() => {
      pollTimer = null;
      void runCycle();
    }, delay);
  }

  function startStalledCheck(): void {
    if (stalledTimer !== null || stopped) return;
    stalledTimer = setInterval(() => refreshConnection(), D.stalledCheckMs);
  }
  function stopStalledCheck(): void {
    if (stalledTimer !== null) clearInterval(stalledTimer);
    stalledTimer = null;
  }

  function refreshConnection(): void {
    const before = state.connection;
    setState({});
    if (state.connection !== before) emit();
  }

  // ── ciclo do snapshot ──
  function runCycle(): Promise<void> {
    if (inflight) return inflight;
    if (stopped) return Promise.resolve();
    pollTimer = clearTimer(pollTimer);
    // O ciclo não lança: o validador devolve erros. Se um dia lançar com uma resposta hostil, a exceção vale como
    // snapshot inválido e a busca seguinte é agendada; sem isto o ciclo morria calado, e o site parava de atualizar.
    const p = cycle()
      .catch(() => {
        onInvalid([]);
        afterCycle();
      })
      .finally(() => {
        inflight = null;
        if (rerun && !stopped) {
          rerun = false;
          void runCycle();
        }
      });
    inflight = p;
    return p;
  }

  async function cycle(): Promise<void> {
    const ac = new AbortController();
    cycleAbort = ac;
    const first = !firstDone;
    firstDone = true;
    if (first && source.kind === 'http') {
      const pre = takePrefetch();
      if (pre && deviceNow() - pre.startedAt <= D.prefetchMaxAgeMs) {
        let text: string | null = null;
        try {
          text = await pre.response;
        } catch {
          text = null;
        }
        if (ac.signal.aborted || stopped) return;
        // o `Date` da pré-busca corrige o relógio antes do 1º snapshot (M4)
        if (pre.at !== undefined) source.learnServerDate?.(pre.date ?? null, pre.age ?? null, pre.at);
        // erro HTTP na pré-busca: conta como a 1ª falha e espera o recuo, sem pedir o mesmo arquivo de novo (B5)
        if (text === null && pre.status !== undefined && pre.status >= 400) {
          onFetchError(new SourceError('http', pre.status === 403 ? 404 : pre.status));
          afterCycle();
          return;
        }
        if (text !== null) {
          const parsed = parseFeedJson(text);
          if (parsed.ok) {
            const res: SourceResponse<unknown> = {
              data: parsed.value,
              etag: null,
              url: 'prefetch',
              receivedAtMs: clock(),
              // o `Date` da pré-busca também entra na validação: sem ele, "publicado no futuro" (I-04) passava na 1ª
              // carga (pré-mortem, fases.mjs no pacote de produção, 20/10)
              serverDateMs: prefetchDateMs(pre),
            };
            // Pré-busca inválida: o ciclo normal segue sem esperar (§15.7.4).
            if (handleResponse(res, true)) {
              afterCycle();
              return;
            }
          }
        }
      }
    }
    let res: SourceResponse<unknown>;
    try {
      res = await source.snapshot({ signal: ac.signal, etag: curEtag });
    } catch (e) {
      if (ac.signal.aborted || stopped || isAbort(e)) return;
      onFetchError(e);
      afterCycle();
      return;
    }
    if (ac.signal.aborted || stopped) return;
    handleResponse(res, false);
    afterCycle();
  }

  function afterCycle(): void {
    if (!stopped) {
      schedule();
      maybeContext();
    }
  }

  function failoverIfDue(): void {
    const n = source.originCount?.() ?? 1;
    if (n > 1 && state.failures >= D.failoverAfterFailures && state.failures % D.failoverAfterFailures === 0) {
      originIdx = (originIdx + 1) % n;
      source.setOrigin?.(originIdx);
    }
  }

  function onFetchError(e: unknown): void {
    const err = e instanceof SourceError ? e : new SourceError('network');
    retryAfterMs = err.retryAfterMs;
    const online = state.online && isOnline();
    // I-18: corpo que não é JSON ou grande demais é recusa de dado (E_JSON, E_SIZE), não de transporte.
    const code = err.kind === 'parse' ? 'E_JSON' : err.kind === 'size' ? 'E_SIZE' : null;
    if (code) log.error('[app] dados inválidos', code, 'snapshot.json');
    setState({
      failures: state.failures + 1,
      reason: code ?? (err.status === undefined ? err.kind : `${err.kind} ${err.status}`),
      online,
    });
    failoverIfDue();
    emit();
  }

  function onInvalid(errors: ValidationIssue[]): void {
    const first = errors[0];
    for (const e of errors.slice(0, 5)) log.error('[app] dados inválidos', e.code, e.path);
    retryAfterMs = undefined;
    setState({
      failures: state.failures + 1,
      reason: first ? `${first.code} ${first.path}`.trim() : 'E_SCHEMA',
    });
    failoverIfDue();
    emit();
  }

  /** Devolve true se a resposta foi aproveitada (aceita, sem mudança ou ignorada por E_SEQ). */
  function handleResponse(res: SourceResponse<unknown>, fromPrefetch: boolean): boolean {
    retryAfterMs = undefined;
    if (res.data === null) {
      // "Sem mudança" (304/ETag): só lastSuccessAt avança. Sem outra mudança, não avisa o binding: o sinal `feed` não
      // muda e nenhum componente re-renderiza (AC-2.4-02 prevalece sobre "a cada mudança de FeedState", §14.8.10).
      const before = state;
      setState({ lastSuccessAt: clock(), failures: 0, reason: null });
      if (
        before.failures !== state.failures ||
        before.reason !== state.reason ||
        before.connection !== state.connection
      ) {
        emit();
      }
      void recheckCommentary();
      return true;
    }
    // I-03 antes do resto: seq menor sem época nova é ignorado (E_SEQ), não é falha; as demais invariantes
    // comparadas com o atual (ex.: permanência das decisões, I-12) não se aplicam a um arquivo que será ignorado.
    const head = res.data as { seq?: unknown; epoch?: unknown } | null;
    if (
      cur &&
      typeof head?.seq === 'number' &&
      typeof head.epoch === 'number' &&
      (head.epoch < cur.epoch || (head.epoch === cur.epoch && head.seq < cur.seq))
    ) {
      log.warn(
        '[app] E_SEQ',
        'seq',
        `seq ${head.seq} (época ${head.epoch}) anterior ao atual ${cur.seq} (época ${cur.epoch})`,
      );
      if (!fromPrefetch) emit();
      return true;
    }
    const checked = validateSnapshot(res.data, ctx(cur, res.serverDateMs));
    if (!checked.ok) {
      const seqOnly = checked.errors.every((e) => e.code === 'E_SEQ');
      if (seqOnly) {
        for (const e of checked.errors) log.warn('[app] E_SEQ', e.path, e.detail);
        if (!fromPrefetch) emit();
        return true;
      }
      if (fromPrefetch) return false;
      onInvalid(checked.errors);
      return false;
    }
    const snap = checked.value;
    if (checked.dropped.length > 0) log.warn('[app] campos descartados', checked.dropped.join(', '));
    // P-16/P-18: na noite gravada (`replay`, ensaio por HTTP), "agora" é a hora do feed, não a do servidor (o dia do
    // ensaio): fase, manchete e contagem a seguem. Fixtures e simulação ficam no relógio da fonte (e do page.clock).
    if (snap.origin === 'replay') {
      source.followFeedClock?.(Math.max(Date.parse(snap.clocks.published_at), msOrNull(snap.clocks.verified_at) ?? 0));
    }
    const now = clock();
    const verifiedAt = msOrNull(snap.clocks.verified_at);
    if (cur && snap.epoch === cur.epoch && snap.seq === cur.seq) {
      // Regravação do mesmo seq (§14.8.3, passo 5): só verifiedAt e ops; fora de deriveEvents e do histórico. Exceção
      // (P-30 item 5, §75): o desligar da chance e dos comentários entra já, só no sentido seguro; a vista é refeita
      // com os mesmos eventos (nada novo para a LiveRegion) e o instante não muda.
      curEtag = res.etag;
      applyOps(snap.ops);
      setState({ lastSuccessAt: now, failures: 0, reason: null, verifiedAt, ...opsPatch(snap.ops) });
      const safer = saferOnRewrite(cur, snap);
      if (safer === null) emit();
      else {
        const detailOff = safer.files.probability_detail === null && probDetail !== null;
        const wasAlive = chanceAlive();
        cur = safer;
        if (wasAlive !== chanceAlive()) replayCache.clear();
        if (safer.files.probability_detail === null) {
          probDetail = null;
          probPath = null;
        }
        liveView = select(safer);
        emitView(liveView, true, detailOff ? { probabilityDetail: null } : {});
        syncCommentaryPath();
      }
      void recheckCommentary();
      return true;
    }
    const rollback = cur !== null && snap.epoch > cur.epoch;
    const prevChangeAt = state.lastChangeAt;
    if (rollback) {
      provisional = [];
      published = published ? published.filter((e) => e.seq <= snap.seq) : null;
      headlineMem = null;
      detailCache.clear();
      detailFails.clear();
      replayCache.clear();
      archiveIndex = null;
      localSeries = localSeries.filter((p) => p.seq <= snap.seq);
      fileSeries = fileSeries.filter((p) => p.seq <= snap.seq);
      trio = [null, null];
      memo = EMPTY_MEMO;
      lastEventsAt = Number.NEGATIVE_INFINITY;
      // colunas e detalhe da probabilidade do ramo abandonado saem com ele
      cols = null;
      colsPath = null;
      colsDerived = null;
      probDetail = null;
      probPath = null;
      insightIdx = null;
      insightCache.clear();
      commentaryIdx = null;
      commentaryFiles.clear();
    }
    const [prev2, prev1] = trio;
    let newEvents: ElectionEvent[] = [];
    const geo = geography();
    if (!rollback && cur !== null) {
      const derived = deriveEvents(prev2, prev1 ?? cur, snap, geo, rules, memo, { prev: colsDerived, curr: cols });
      memo = derived.memo;
      colsDerived = cols;
      newEvents = derived.events;
      provisional = addEvents(provisional, newEvents);
    }
    trio = [prev1 ?? null, snap];
    const wasAlive = chanceAlive();
    cur = snap;
    if (wasAlive !== chanceAlive()) replayCache.clear();
    curEtag = res.etag;
    applyOps(snap.ops);
    const point = historyPointOf(snap);
    if (point) localSeries = mergeSeries([point], localSeries);
    series = mergeSeries(fileSeries, localSeries);
    if (eventsFailed) provisional = addEvents(provisional, retroEvents());
    const interval = prevChangeAt !== null ? now - prevChangeAt : null;
    const mean =
      interval === null || interval <= 0
        ? state.meanSeqIntervalMs
        : state.meanSeqIntervalMs === null
          ? interval
          : 0.7 * state.meanSeqIntervalMs + 0.3 * interval;
    const ht = snap.clocks.tse_ht;
    liveView = select(snap);
    setState({
      seq: snap.seq,
      epoch: snap.epoch,
      lastSuccessAt: now,
      // a hora do dado, e não a da chegada (ORD-F-147): nunca no futuro
      lastChangeAt: dataAt(snap, now),
      failures: 0,
      reason: null,
      verifiedAt,
      tseHtTMin: ht ? tMin(ht, date) : null,
      meanSeqIntervalMs: mean,
      allComplete: allCompleteOf(snap, cfg.features.governors),
      ...opsPatch(snap.ops),
    });
    emitView(
      liveView,
      newEvents.some((e) => e.salience === 3),
      { historySeries: series },
    );
    secondary(rollback);
    return true;
  }

  function opsPatch(ops: Ops): Partial<FeedState> {
    return {
      notice: ops.notice ? { text: ops.notice.text, level: ops.notice.level, since: ops.notice.since } : null,
      sourceStatus: ops.source_status,
      held: ops.held ?? [],
    };
  }

  /** §14.8.5: só chamado com snapshot válido (M9). */
  function applyOps(ops: Ops): void {
    pollHintMs =
      typeof ops.poll_hint_s === 'number' && Number.isFinite(ops.poll_hint_s)
        ? Math.min(300, Math.max(5, ops.poll_hint_s)) * 1000
        : null;
    const build = ops.reload_build;
    if (build !== null && build !== cfg.build && !reloadSeen.has(build)) {
      reloadSeen.add(build);
      o.onReloadRequested?.(build, 2_000 + random() * 58_000);
    }
  }

  // ── secundários (§14.8.6) ──
  function secondary(force: boolean): void {
    syncInsightsPath();
    syncCommentaryPath();
    void maybeMunicipal();
    void maybeProbabilityDetail();
    if (contextOn) void maybeEvents(force);
  }

  function maybeContext(): void {
    if (contextOn || contextTimer !== null || !liveView || stopped) return;
    contextTimer = setTimeout(() => {
      contextTimer = null;
      contextReady();
    }, D.contextDelayMs);
  }

  function contextReady(): void {
    contextTimer = clearTimer(contextTimer);
    if (contextOn) return;
    contextOn = true;
    void maybeHistory();
    void maybeEvents(false);
  }

  const secondaryAllowed = () => started && !stopped && !hidden && !suspended && state.online;

  async function maybeMunicipal(): Promise<void> {
    const path = cur?.files.municipal ?? null;
    if (!wantMun || path === null || path === colsPath || municipalInflight || !secondaryAllowed()) return;
    const wait = lastMunicipalAt + D.municipalMinIntervalMs - deviceNow();
    if (wait > 0) {
      if (municipalTimer === null) {
        municipalTimer = setTimeout(() => {
          municipalTimer = null;
          void maybeMunicipal();
        }, wait);
      }
      return;
    }
    municipalInflight = true;
    lastMunicipalAt = deviceNow();
    try {
      const res = await source.municipal(path, { signal: new AbortController().signal });
      if (stopped || res.data === null) return;
      const v = validateMunicipal(res.data, ctx(null, res.serverDateMs));
      if (!v.ok) {
        for (const e of v.errors.slice(0, 3)) log.error('[app] dados inválidos', e.code, `municipal ${e.path}`);
        return;
      }
      // Colunas de outra época (ramo abandonado num rollback, I-03) nunca se misturam ao snapshot atual.
      if (!cur || v.value.epoch !== cur.epoch) return;
      cols = v.value;
      colsPath = path;
      rebuildView();
    } catch {
      // mantém o anterior; tenta no próximo seq
    } finally {
      municipalInflight = false;
    }
  }

  async function maybeProbabilityDetail(): Promise<void> {
    const path = cur?.files.probability_detail ?? null;
    if (!wantProb || path === null || path === probPath || probInflight || !secondaryAllowed()) return;
    probInflight = true;
    try {
      const res = await source.probabilityDetail(path, { signal: new AbortController().signal });
      if (stopped || res.data === null) return;
      const v = validateProbabilityDetail(res.data, ctx(null, res.serverDateMs));
      // o detalhe desligado durante a busca (§75) não volta
      if (!v.ok || !cur || v.value.epoch !== cur.epoch || cur.files.probability_detail === null) return;
      probDetail = v.value;
      probPath = path;
      emit({ probabilityDetail: probDetail });
    } catch {
      // silenciosa
    } finally {
      probInflight = false;
    }
  }

  /** Avisa o binding quando a camada de insights liga, desliga ou muda de arquivo (o painel só monta com ela). */
  function syncInsightsPath(): void {
    const raw = cur?.files.insights ?? null;
    const path = raw !== null && INSIGHT_INDEX_PATH.test(raw) ? raw : null;
    if (path === insightsPathSent) return;
    insightsPathSent = path;
    emit({ insightsPath: path });
  }

  /** Avisa o binding quando os comentários ligam, desligam ou mudam de arquivo (o painel só monta com eles). */
  function syncCommentaryPath(): void {
    const raw = cur?.files.commentary ?? null;
    const path = raw !== null && COMMENTARY_INDEX_PATH.test(raw) ? raw : null;
    if (path === commentaryPathSent) return;
    commentaryPathSent = path;
    emit({ commentaryPath: path });
  }

  function commentaryIndex(validate: InsightValidator<CommentaryIndex>): Promise<CommentaryIndex | null> {
    commentaryValidate = validate;
    const path = cur?.files.commentary ?? null;
    if (!cur || path === null || !COMMENTARY_INDEX_PATH.test(path) || !source.commentary) return Promise.resolve(null);
    const { seq, epoch } = cur;
    if (commentaryIdx && commentaryIdx.seq === seq && commentaryIdx.path === path)
      return Promise.resolve(commentaryIdx.value);
    const key = `${epoch}:${seq}:${path}`;
    if (commentaryInflight?.key === key) return commentaryInflight.p;
    const get = source.commentary.bind(source);
    const p = (async () => {
      try {
        const res = await get(path, { signal: new AbortController().signal });
        // 304: o índice não mudou desde o último seq
        if (res.data === null) return commentaryIdx?.path === path ? commentaryIdx.value : null;
        const v = validate(res.data, ctx(null, res.serverDateMs));
        if (!v.ok || v.value.epoch !== epoch) return null;
        // nunca troca um índice mais novo por um que chegou atrasado
        if (!stopped && cur?.epoch === epoch && !(commentaryIdx && commentaryIdx.seq > seq))
          commentaryIdx = { seq, path, value: v.value };
        return v.value;
      } catch {
        return null;
      } finally {
        if (commentaryInflight?.key === key) commentaryInflight = null;
      }
    })();
    commentaryInflight = { key, p };
    return p;
  }

  /**
   * "Desligar lento" (§75): com o `seq` parado (304 ou regravação do mesmo `seq`), relê o índice dos comentários a cada
   * busca do snapshot (o ritmo do `poll_hint`), se o painel já o pediu neste `seq` e ele não está `off`. Adota só o lado
   * seguro: `active` → `paused`/`off` e `paused` → `off`, com os itens de antes (nada novo na tela nem na LiveRegion).
   * Voltar a `active` e itens novos esperam o próximo `seq`.
   */
  async function recheckCommentary(): Promise<void> {
    const known = commentaryIdx;
    const validate = commentaryValidate;
    const path = cur?.files.commentary ?? null;
    if (!cur || !known || !validate || !source.commentary || commentaryRecheckInflight || !secondaryAllowed()) return;
    if (path === null || known.path !== path || known.seq !== cur.seq || known.value.status === 'off') return;
    const { seq, epoch } = cur;
    commentaryRecheckInflight = true;
    try {
      const res = await source.commentary(path, { signal: new AbortController().signal });
      if (stopped || res.data === null) return;
      const v = validate(res.data, ctx(null, res.serverDateMs));
      if (!v.ok || v.value.epoch !== epoch) return;
      // nada mudou durante a busca (seq novo, rollback, outro índice): senão o pedido por seq já cuida
      if (commentaryIdx !== known || cur?.seq !== seq || cur.epoch !== epoch || cur.files.commentary !== path) return;
      const status = v.value.status;
      if (status === 'active' || status === known.value.status) return;
      commentaryIdx = {
        seq,
        path,
        value: {
          ...known.value,
          status,
          status_at: v.value.status_at,
          ...(status === 'off' ? { hours: null } : {}),
        },
      };
      commentaryRev += 1;
      emit({ commentaryRev });
    } catch {
      // silenciosa: tenta de novo na próxima busca
    } finally {
      commentaryRecheckInflight = false;
    }
  }

  function insightIndex(validate: InsightValidator<InsightIndex>): Promise<InsightIndex | null> {
    const path = cur?.files.insights ?? null;
    if (!cur || path === null || !INSIGHT_INDEX_PATH.test(path) || !source.insights) return Promise.resolve(null);
    const { seq, epoch } = cur;
    if (insightIdx && insightIdx.seq === seq && insightIdx.path === path) return Promise.resolve(insightIdx.value);
    const key = `${epoch}:${seq}:${path}`;
    if (insightIdxInflight?.key === key) return insightIdxInflight.p;
    const get = source.insights.bind(source);
    const p = (async () => {
      try {
        const res = await get(path, { signal: new AbortController().signal });
        // 304: o índice não mudou desde o último seq
        if (res.data === null) return insightIdx?.path === path ? insightIdx.value : null;
        const v = validate(res.data, ctx(null, res.serverDateMs));
        if (!v.ok || v.value.epoch !== epoch) return null;
        // nunca troca um índice mais novo por um que chegou atrasado
        if (!stopped && cur?.epoch === epoch && !(insightIdx && insightIdx.seq > seq))
          insightIdx = { seq, path, value: v.value };
        return v.value;
      } catch {
        return null;
      } finally {
        if (insightIdxInflight?.key === key) insightIdxInflight = null;
      }
    })();
    insightIdxInflight = { key, p };
    return p;
  }

  function insight(rel: string, validate: InsightValidator<Insight>): Promise<Insight | null> {
    const index = cur?.files.insights ?? null;
    if (index === null || !INSIGHT_INDEX_PATH.test(index) || !INSIGHT_SIGNAL_PATH.test(rel) || !source.insights)
      return Promise.resolve(null);
    const path = `${index.slice(0, index.lastIndexOf('/') + 1)}${rel}`;
    const hit = insightCache.get(path);
    if (hit) return Promise.resolve(hit);
    const running = insightInflight.get(path);
    if (running) return running;
    const get = source.insights.bind(source);
    const epochAsked = cur?.epoch ?? null;
    const p = (async () => {
      try {
        const res = await get(path, { signal: new AbortController().signal });
        if (res.data === null) return null;
        const v = validate(res.data, ctx(null, res.serverDateMs));
        if (!v.ok) return null;
        // um pacote pedido antes de um rollback não entra no cache da época nova
        if (cur?.epoch !== epochAsked) return v.value;
        insightCache.set(path, v.value);
        while (insightCache.size > INSIGHT_CACHE_SIZE) {
          const oldest = insightCache.keys().next().value;
          if (oldest === undefined) break;
          insightCache.delete(oldest);
        }
        return v.value;
      } catch {
        return null;
      } finally {
        insightInflight.delete(path);
      }
    })();
    insightInflight.set(path, p);
    return p;
  }

  function commentaryFile<T>(path: string, validate: InsightValidator<T>): Promise<T | null> {
    if (!cur || cur.files.commentary == null || !COMMENTARY_FILE_PATH.test(path) || !source.commentary)
      return Promise.resolve(null);
    const key = `${cur.epoch}:${path}`;
    if (commentaryFiles.has(key)) return Promise.resolve(commentaryFiles.get(key) as T);
    const running = commentaryFilesInflight.get(key);
    if (running) return running as Promise<T | null>;
    const get = source.commentary.bind(source);
    const epochAsked = cur.epoch;
    const p = (async () => {
      try {
        const res = await get(path, { signal: new AbortController().signal });
        if (res.data === null) return null;
        const v = validate(res.data, ctx(null, res.serverDateMs));
        // a hora de outra época (antes de um recomeço do motor) nunca se mistura à de agora
        if (!v.ok || (v.value as { epoch?: number }).epoch !== epochAsked) return null;
        // um arquivo pedido antes de um rollback não entra no cache da época nova
        if (cur?.epoch !== epochAsked) return v.value;
        commentaryFiles.set(key, v.value);
        while (commentaryFiles.size > COMMENTARY_FILE_CACHE_SIZE) {
          const oldest = commentaryFiles.keys().next().value;
          if (oldest === undefined) break;
          commentaryFiles.delete(oldest);
        }
        return v.value;
      } catch {
        return null;
      } finally {
        commentaryFilesInflight.delete(key);
      }
    })();
    commentaryFilesInflight.set(key, p);
    return p;
  }

  async function maybeHistory(): Promise<void> {
    if (!contextOn || historyInflight || !secondaryAllowed()) return;
    const wait = lastHistoryAt + D.historyIntervalMs - deviceNow();
    if (wait > 0) {
      scheduleHistory(wait);
      return;
    }
    historyInflight = true;
    lastHistoryAt = deviceNow();
    try {
      const res = await source.history({ signal: new AbortController().signal });
      if (stopped || res.data === null) return;
      const v = validateHistory(res.data, ctx(null, res.serverDateMs));
      if (!v.ok) return;
      // Histórico de outra época (I-03): ignorado em silêncio; a série local continua e a próxima busca tenta de novo.
      if (cur && v.value.epoch !== cur.epoch) return;
      fileSeries = cur ? v.value.points.filter((p) => p.seq <= (cur as Snapshot).seq) : v.value.points;
      series = mergeSeries(fileSeries, localSeries);
      if (eventsFailed) {
        provisional = addEvents(provisional, retroEvents());
        if (cur) liveView = select(cur);
      }
      emit(liveView && eventsFailed ? { historySeries: series, liveView } : { historySeries: series });
    } catch {
      // silenciosa: a série local continua
    } finally {
      historyInflight = false;
      scheduleHistory(D.historyIntervalMs);
    }
  }

  function scheduleHistory(ms: number): void {
    historyTimer = clearTimer(historyTimer);
    if (!secondaryAllowed()) return;
    historyTimer = setTimeout(() => {
      historyTimer = null;
      void maybeHistory();
    }, ms);
  }

  async function maybeEvents(force: boolean): Promise<void> {
    if (!contextOn || eventsInflight || !secondaryAllowed() || !cur) return;
    if (!force && lastEventsSeq === cur.seq) return;
    const wait = lastEventsAt + D.eventsMinIntervalMs - deviceNow();
    if (!force && wait > 0) {
      if (eventsTimer === null) {
        eventsTimer = setTimeout(() => {
          eventsTimer = null;
          void maybeEvents(false);
        }, wait);
      }
      return;
    }
    eventsInflight = true;
    lastEventsAt = deviceNow();
    lastEventsSeq = cur.seq;
    try {
      const res = await source.events({ signal: new AbortController().signal });
      if (stopped) return;
      if (res.data !== null) {
        const v = validateEvents(res.data, ctx(null, res.serverDateMs));
        if (!v.ok) throw new Error('events inválido');
        const list = v.value;
        if (cur && list.epoch < cur.epoch) throw new Error('events de época anterior');
        published = list.events.filter((e) => cur === null || e.seq <= cur.seq);
        publishedSeq = list.seq;
        provisional = provisional.filter((e) => e.seq > list.seq);
        eventsFailed = false;
      }
      rebuildView();
    } catch {
      // silenciosa: ficam os derivados; viradas, marcos e lotes recalculados sobre a série (§14.8.8)
      eventsFailed = true;
      const before = provisional.length;
      provisional = addEvents(provisional, retroEvents());
      if (provisional.length !== before) rebuildView();
    } finally {
      eventsInflight = false;
    }
  }

  function retroEvents(): ElectionEvent[] {
    if (!cur) return [];
    const pair = cur.races.president.candidates as readonly [CandidateNumber, CandidateNumber];
    return deriveFromHistory(series, pair, cur.races.president.national.sections, rules);
  }

  // ── replay (§14.8.7) ──
  async function getArchiveIndex(signal?: AbortSignal): Promise<ArchiveIndex> {
    if (archiveIndex && deviceNow() - archiveIndex.at < D.archiveIndexTtlMs) return archiveIndex.value;
    if (!source.archive) throw new ReplayError('E_NO_RECORD');
    const res = await source.archive.index({ signal: signal ?? new AbortController().signal });
    if (res.data === null) {
      if (archiveIndex) return archiveIndex.value;
      throw new ReplayError('E_NO_RECORD');
    }
    const v = validateArchiveIndex(res.data, ctx(null, res.serverDateMs));
    if (!v.ok) throw new ReplayError('E_NO_RECORD');
    archiveIndex = { at: deviceNow(), value: v.value };
    return v.value;
  }

  /** A chance segue no ar no snapshot atual (o kill switch a tira: `probability: null` em todo seq seguinte). */
  function chanceAlive(): boolean {
    return cur !== null && cur.probability !== null;
  }

  function replayView(s: Snapshot, instant: number, municipal: MunicipalColumns | null): ResultsView {
    return selectViewWithMemory(s, {
      candidates: o.candidates,
      geography: geography(),
      municipal,
      events: events(),
      instantTMin: instant,
      clockTMin: clockTMin(),
      // depois do kill switch (a chance some do snapshot atual), a linha do tempo também não a mostra nos seq passados
      // (runbook 06, "Pausa da probabilidade"; pré-mortem, ORD-F-146)
      probabilityOn: cfg.features.probability && chanceAlive(),
      headlineMem: null,
      mode: 'replay',
      headlinePolicy: o.headlinePolicy,
      probabilityPolicy: o.probabilityPolicy,
    }).view;
  }

  function validReplaySnapshot(raw: unknown): Snapshot {
    const v = validateSnapshot(raw, ctx(null));
    if (!v.ok) throw new ReplayError('E_INVALID');
    return v.value;
  }

  function validReplayMunicipal(raw: unknown | null): MunicipalColumns | null {
    if (raw === null || raw === undefined) return null;
    const v = validateMunicipal(raw, ctx(null));
    return v.ok ? v.value : null;
  }

  async function at(requested: number, signal?: AbortSignal): Promise<ReplayResult> {
    const token = ++atToken;
    const check = () => {
      if (signal?.aborted) {
        const err = new Error('aborted');
        err.name = 'AbortError';
        throw err;
      }
      if (token !== atToken) throw new ReplayError('E_STALE');
    };
    if (liveView && requested >= liveView.instantTMin) {
      return { view: liveView, requestedTMin: requested, shownTMin: liveView.instantTMin, seq: liveView.seq };
    }
    const files = source.instantAt?.(requested) ?? null;
    if (files) {
      const snap = validReplaySnapshot(files.snapshot);
      const shown = liveInstantTMin(snap);
      const view = replayView(snap, shown, validReplayMunicipal(files.municipal));
      check();
      return { view, requestedTMin: requested, shownTMin: shown, seq: snap.seq };
    }
    const index = await getArchiveIndex(signal);
    check();
    const items = index.items;
    let lo = 0;
    let hi = items.length - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if ((items[mid]?.t_min ?? Number.POSITIVE_INFINITY) <= requested) {
        found = mid;
        lo = mid + 1;
      } else hi = mid - 1;
    }
    const item = items[found];
    if (!item || !source.archive) throw new ReplayError('E_NO_RECORD');
    const cached = replayCache.get(item.seq);
    if (cached) {
      replayCache.delete(item.seq);
      replayCache.set(item.seq, cached);
      return { view: cached, requestedTMin: requested, shownTMin: item.t_min, seq: item.seq };
    }
    const sig = signal ?? new AbortController().signal;
    const res = await source.archive.snapshotAt(item.path, { signal: sig });
    check();
    if (res.data === null) throw new ReplayError('E_NO_RECORD');
    const snap = validReplaySnapshot(res.data);
    let municipal: MunicipalColumns | null = null;
    if (item.municipal && wantMun) {
      try {
        const m = await source.municipal(item.municipal, { signal: sig });
        municipal = validReplayMunicipal(m.data);
      } catch (e) {
        if (isAbort(e)) throw e;
      }
      check();
    }
    const view = replayView(snap, item.t_min, municipal);
    replayCache.set(item.seq, view);
    while (replayCache.size > D.replayCacheSize) {
      const oldest = replayCache.keys().next().value;
      if (oldest === undefined) break;
      replayCache.delete(oldest);
    }
    return { view, requestedTMin: requested, shownTMin: item.t_min, seq: item.seq };
  }

  // ── detalhe sob demanda ──
  function stateDetail(place: PlaceCode, office: Office = 'president'): Promise<StateDetail | null> {
    if (!cur) return Promise.resolve(null);
    const seq = cur.seq;
    const epochAsked = cur.epoch;
    const key = `${seq}:${place}:${office}`;
    const now = deviceNow();
    const hit = detailCache.get(key);
    if (hit && now - hit.at < D.detailTtlMs) {
      detailCache.delete(key);
      detailCache.set(key, hit);
      return Promise.resolve(hit.value);
    }
    const failKey = `${place}:${office}`;
    const fail = detailFails.get(failKey);
    if (fail && now < fail.retryAt) return Promise.resolve(null);
    const running = detailInflight.get(key);
    if (running) return running;
    const p = (async () => {
      try {
        const res = await source.stateDetail(seq, place, office, { signal: new AbortController().signal });
        if (res.data === null) return hit?.value ?? null;
        const v = validateStateDetail(res.data, place, ctx(null, res.serverDateMs));
        if (!v.ok) throw new Error('state inválido');
        if (v.value.epoch !== epochAsked) throw new Error('state de outra época');
        detailFails.delete(failKey);
        detailCache.set(key, { at: deviceNow(), value: v.value });
        while (detailCache.size > D.detailCacheSize) {
          const oldest = detailCache.keys().next().value;
          if (oldest === undefined) break;
          detailCache.delete(oldest);
        }
        return v.value;
      } catch {
        const k = (detailFails.get(failKey)?.k ?? 0) + 1;
        const wait = Math.min(D.detailRetryMaxMs, D.detailRetryBaseMs * 2 ** (k - 1));
        detailFails.set(failKey, { k, retryAt: deviceNow() + wait });
        return null;
      } finally {
        detailInflight.delete(key);
      }
    })();
    detailInflight.set(key, p);
    return p;
  }

  async function zones(ibge: IbgeCode): Promise<ZoneResults | null> {
    if (!source.zones) return null;
    const hit = zonesCache.get(ibge);
    if (hit && deviceNow() - hit.at < D.detailTtlMs) return hit.value;
    try {
      const res = await source.zones(ibge, { signal: new AbortController().signal });
      if (res.data === null) return hit?.value ?? null;
      const parsed = ZoneResultsSchema.safeParse(res.data);
      if (!parsed.success) return null;
      const value = parsed.data as ZoneResults;
      zonesCache.set(ibge, { at: deviceNow(), value });
      return value;
    } catch {
      return null;
    }
  }

  // ── ciclo de vida ──
  function haltTimers(): void {
    pollTimer = clearTimer(pollTimer);
    historyTimer = clearTimer(historyTimer);
    eventsTimer = clearTimer(eventsTimer);
    municipalTimer = clearTimer(municipalTimer);
    stopStalledCheck();
  }

  function refreshNow(): Promise<void> {
    if (!started || stopped) return Promise.resolve();
    suspended = false;
    if (!hidden) startStalledCheck();
    const p = runCycle();
    if (contextOn) {
      void maybeHistory();
      void maybeEvents(false);
    }
    return p;
  }

  return {
    start() {
      if (started && !stopped) return;
      started = true;
      stopped = false;
      setState({ online: isOnline() });
      if (source.subscribe) {
        unsubscribe = source.subscribe((reason) => {
          if (stopped || hidden || suspended || !state.online) return;
          // Com velocidade ≤ 5 o cliente faz polling (o caminho real); o aviso de publicação só reabre o polling se
          // nenhum temporizador estiver armado (ex.: a velocidade acabou de cair de > 5 para ≤ 5).
          if (reason !== 'control' && speed() <= 5) {
            if (!inflight && pollTimer === null) schedule();
            return;
          }
          if (inflight) rerun = true;
          else void runCycle();
        });
      }
      if (!hidden) startStalledCheck();
      // M8: a 1ª busca sai na hora, mesmo com a aba oculta.
      void runCycle();
    },
    stop() {
      stopped = true;
      cycleAbort?.abort();
      cycleAbort = null;
      haltTimers();
      contextTimer = clearTimer(contextTimer);
      emitTimer = clearTimer(emitTimer);
      unsubscribe?.();
      unsubscribe = null;
    },
    refreshNow,
    at,
    stateDetail,
    zones,
    insightIndex,
    insight,
    commentaryIndex,
    commentaryFile,
    wantMunicipal(on) {
      wantMun = on;
      if (on) void maybeMunicipal();
    },
    wantProbabilityDetail(on) {
      wantProb = on;
      if (on) void maybeProbabilityDetail();
    },
    setVisible(visible) {
      if (visible === !hidden) return;
      hidden = !visible;
      if (hidden) {
        haltTimers();
        return;
      }
      void refreshNow();
    },
    setOnline(online) {
      if (online === state.online) return;
      setState({ online });
      emit();
      if (!online) {
        haltTimers();
        return;
      }
      void refreshNow();
    },
    pageShow(persisted) {
      if (!persisted) return;
      archiveIndex = null;
      void refreshNow();
    },
    pageHide(persisted) {
      if (!persisted) return;
      suspended = true;
      cycleAbort?.abort();
      haltTimers();
    },
    contextReady,
    refreshConnection,
    refreshView() {
      rebuildView();
    },
    state: () => state,
    view: () => liveView,
    probabilityDetail: () => probDetail,
  };
}
